/**
 * PartnerNetworkTab — Admin UI for managing partner organizations,
 * attaching vehicles, and viewing each partner's monthly revenue split.
 */
import React, { useEffect, useState } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Building2, Plus, Trash2, X, RefreshCw, ExternalLink, Copy, KeyRound,
  Car, Percent, DollarSign, Globe,
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const f$ = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

const PartnerNetworkTab = () => {
  const [dash, setDash] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [active, setActive] = useState(null); // org currently being edited
  const [split, setSplit] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const [d, o, v] = await Promise.all([
          fetch(`${API}/api/partner-network/dashboard`).then(r => r.json()),
          fetch(`${API}/api/partner-network/orgs`).then(r => r.json()),
          fetch(`${API}/api/pricing/vehicles`).then(r => r.json()),
        ]);
        if (cancelled) return;
        setDash(d);
        setOrgs(o.orgs || []);
        setVehicles(v.vehicles || v || []);
      } catch { if (!cancelled) toast.error('Failed to load partner network'); }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [reloadKey]);

  const reload = () => setReloadKey(k => k + 1);

  return (
    <div className="space-y-4 p-2" data-testid="partner-network-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-black tracking-tight">Partner Network</h2>
          <p className="text-xs text-gray-500 mt-1">Multi-tenant inventory + monthly revenue split tracking.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={reload} variant="outline" className="h-9 text-xs" data-testid="pn-reload">
            <RefreshCw className={`h-3 w-3 mr-1 ${loading ? 'animate-spin' : ''}`} /> Reload
          </Button>
          <Button onClick={() => setShowNew(true)} className="bg-red-600 hover:bg-red-700 h-9 text-xs" data-testid="pn-new-org">
            <Plus className="h-3 w-3 mr-1" /> Add Partner
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      {dash && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          <Kpi label="Active Partners" value={dash.active_orgs} sub={`of ${dash.total_orgs} total`} />
          <Kpi label="Partner Vehicles" value={dash.partner_vehicles} icon={<Car className="h-3 w-3" />} />
          <Kpi label="Bookings This Month" value={dash.partner_bookings_this_month} sub={dash.month} />
          <Kpi label="Partner Revenue" value={f$(dash.partner_revenue_this_month)} color="amber" />
          <Kpi label="ACE Revenue" value={f$(dash.ace_revenue_this_month || 0)} color="emerald" />
        </div>
      )}

      {/* Partner orgs list */}
      <Card className="p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-3 py-2 font-semibold text-xs uppercase tracking-wider text-gray-600">Partner</th>
                <th className="text-left px-3 py-2 font-semibold text-xs uppercase tracking-wider text-gray-600">Slug</th>
                <th className="text-left px-3 py-2 font-semibold text-xs uppercase tracking-wider text-gray-600">Vehicles</th>
                <th className="text-left px-3 py-2 font-semibold text-xs uppercase tracking-wider text-gray-600">Default Split</th>
                <th className="text-left px-3 py-2 font-semibold text-xs uppercase tracking-wider text-gray-600">Active Bookings</th>
                <th className="text-left px-3 py-2 font-semibold text-xs uppercase tracking-wider text-gray-600">Status</th>
                <th className="text-right px-3 py-2 font-semibold text-xs uppercase tracking-wider text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody>
              {orgs.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-8 text-gray-400"><Building2 className="h-8 w-8 mx-auto mb-2 text-gray-300" /> No partner orgs yet. Add your first one above.</td></tr>
              ) : orgs.map(o => (
                <tr key={o.org_id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-3 py-2">
                    <div className="font-bold">{o.partner_name}</div>
                    {o.tagline && <div className="text-xs text-gray-500">{o.tagline}</div>}
                  </td>
                  <td className="px-3 py-2 font-mono text-xs text-gray-600">/partners/{o.slug}</td>
                  <td className="px-3 py-2 font-semibold">{o.vehicle_count || 0}</td>
                  <td className="px-3 py-2"><Percent className="h-3 w-3 inline mr-0.5 text-gray-400" />{o.default_revenue_share_percent}%</td>
                  <td className="px-3 py-2">{o.active_bookings || 0}</td>
                  <td className="px-3 py-2">
                    <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded ${o.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600'}`}>{o.status}</span>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <a href={`/partners/${o.slug}`} target="_blank" rel="noreferrer" className="text-xs font-bold text-gray-600 hover:text-gray-900 flex items-center gap-1" data-testid={`pn-view-${o.slug}`}>
                        Public <ExternalLink className="h-3 w-3" />
                      </a>
                      <button onClick={() => setActive(o)} className="text-xs font-bold text-red-600 hover:text-red-700" data-testid={`pn-manage-${o.org_id}`}>Manage</button>
                      <button
                        onClick={async () => {
                          const ym = new Date().toISOString().slice(0, 7);
                          const r = await fetch(`${API}/api/partner-network/orgs/${o.org_id}/revenue-split?ym=${ym}`);
                          setSplit(await r.json());
                        }}
                        className="text-xs font-bold text-blue-600 hover:text-blue-700"
                        data-testid={`pn-split-${o.org_id}`}
                      >Split</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {showNew && <NewOrgForm vehicles={vehicles} onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); reload(); }} />}
      {active && (
        <ManageOrgDrawer
          org={active}
          vehicles={vehicles}
          onClose={() => setActive(null)}
          onChanged={() => { reload(); }}
        />
      )}
      {split && <RevenueSplitDrawer split={split} onClose={() => setSplit(null)} />}
    </div>
  );
};

const Kpi = ({ label, value, sub, icon, color }) => (
  <div className="p-3 rounded-lg border border-gray-200 bg-white">
    <p className="text-[10px] uppercase tracking-widest text-gray-500 font-bold flex items-center gap-1">{icon}{label}</p>
    <p className={`text-2xl font-black mt-0.5 ${color === 'emerald' ? 'text-emerald-600' : color === 'amber' ? 'text-amber-600' : 'text-gray-900'}`}>{value}</p>
    {sub && <p className="text-[10px] text-gray-500 mt-0.5">{sub}</p>}
  </div>
);

function NewOrgForm({ onClose, onCreated }) {
  const [form, setForm] = useState({
    partner_name: '', slug: '', tagline: '', brand_color: '#dc2626',
    contact_name: '', contact_email: '', contact_phone: '', website: '',
    default_revenue_share_percent: 70,
  });
  const [busy, setBusy] = useState(false);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const submit = async () => {
    if (!form.partner_name.trim()) { toast.error('Partner name required'); return; }
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/partner-network/orgs`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, default_revenue_share_percent: Number(form.default_revenue_share_percent) }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Create failed');
      toast.success('Partner org created');
      onCreated();
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <Card className="p-5 w-full max-w-lg" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold">New Partner Org</h3>
          <button onClick={onClose}><X className="h-5 w-5 text-gray-400" /></button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Input placeholder="Partner name *" value={form.partner_name} onChange={e => set('partner_name', e.target.value)} className="col-span-2" data-testid="pn-form-name" />
          <Input placeholder="Slug (auto-generated)" value={form.slug} onChange={e => set('slug', e.target.value)} />
          <Input placeholder="Brand color" value={form.brand_color} onChange={e => set('brand_color', e.target.value)} />
          <Input placeholder="Tagline" value={form.tagline} onChange={e => set('tagline', e.target.value)} className="col-span-2" />
          <Input placeholder="Contact name" value={form.contact_name} onChange={e => set('contact_name', e.target.value)} />
          <Input placeholder="Contact email" value={form.contact_email} onChange={e => set('contact_email', e.target.value)} />
          <Input placeholder="Contact phone" value={form.contact_phone} onChange={e => set('contact_phone', e.target.value)} />
          <Input placeholder="Website" value={form.website} onChange={e => set('website', e.target.value)} />
          <div className="col-span-2">
            <label className="text-[10px] uppercase tracking-widest font-bold text-gray-500 mb-1 block">Default revenue share to partner (%)</label>
            <Input type="number" value={form.default_revenue_share_percent} onChange={e => set('default_revenue_share_percent', e.target.value)} />
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 mt-5">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-red-600 hover:bg-red-700" data-testid="pn-form-submit">
            {busy ? 'Saving…' : 'Create Partner'}
          </Button>
        </div>
      </Card>
    </div>
  );
}

function ManageOrgDrawer({ org, vehicles, onClose, onChanged }) {
  const [orgVehicles, setOrgVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/api/partner-network/orgs/${org.org_id}/vehicles`);
        const d = await r.json();
        if (!cancelled) setOrgVehicles(d.vehicles || []);
      } catch { /* */ }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [org.org_id, reloadKey]);

  const attached = new Set(orgVehicles.map(v => v.vehicle_id));
  const available = vehicles.filter(v => !attached.has(v.vehicle_id) && !v.partner_org_id);

  const attach = async (vehicle_id, share) => {
    await fetch(`${API}/api/partner-network/orgs/${org.org_id}/vehicles`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ vehicle_id, revenue_share_percent: Number(share || org.default_revenue_share_percent) }),
    });
    setReloadKey(k => k + 1); onChanged();
    toast.success('Vehicle attached');
  };
  const detach = async (vehicle_id) => {
    if (!window.confirm('Detach this vehicle from the partner?')) return;
    await fetch(`${API}/api/partner-network/orgs/${org.org_id}/vehicles/${vehicle_id}`, { method: 'DELETE' });
    setReloadKey(k => k + 1); onChanged();
    toast.success('Vehicle detached');
  };
  const rotateKey = async () => {
    if (!window.confirm('Rotate API key? Existing integrations will break.')) return;
    const r = await fetch(`${API}/api/partner-network/orgs/${org.org_id}/rotate-api-key`, { method: 'POST' });
    const d = await r.json();
    toast.success(`New API key: ${d.api_key.slice(0, 12)}…`);
  };
  const del = async () => {
    if (!window.confirm(`Permanently delete ${org.partner_name}? Vehicles will be unlinked.`)) return;
    await fetch(`${API}/api/partner-network/orgs/${org.org_id}`, { method: 'DELETE' });
    toast.success('Partner deleted');
    onClose(); onChanged();
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-stretch justify-end" onClick={onClose}>
      <div className="bg-white w-full max-w-2xl h-full overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-gray-200 px-5 py-4 flex items-start justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">Manage Partner</p>
            <h3 className="text-xl font-black mt-1">{org.partner_name}</h3>
            <p className="text-xs text-gray-500 font-mono">/partners/{org.slug} · {org.default_revenue_share_percent}% default split</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 p-1"><X className="h-5 w-5" /></button>
        </div>

        <div className="p-5 space-y-5">
          <Card className="p-3">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs uppercase tracking-widest font-bold text-gray-500">Integration</p>
              <Button variant="outline" size="sm" onClick={rotateKey} className="h-7 text-xs" data-testid="pn-rotate-key">
                <KeyRound className="h-3 w-3 mr-1" /> Rotate
              </Button>
            </div>
            <div className="bg-gray-50 border rounded p-2 font-mono text-[11px] text-gray-700 break-all flex items-center gap-2">
              <span className="flex-1">{org.api_key || '—'}</span>
              <button
                onClick={() => { navigator.clipboard.writeText(org.api_key || ''); toast.success('Copied'); }}
                className="text-gray-500 hover:text-gray-900"
              >
                <Copy className="h-3 w-3" />
              </button>
            </div>
            {org.intake_referral_code && (
              <p className="text-[10px] text-gray-500 mt-2">Referral code: <span className="font-mono">{org.intake_referral_code}</span></p>
            )}
          </Card>

          <div>
            <p className="text-xs uppercase tracking-widest font-bold text-gray-500 mb-2">Attached Vehicles ({orgVehicles.length})</p>
            {loading ? (
              <p className="text-xs text-gray-400">Loading…</p>
            ) : orgVehicles.length === 0 ? (
              <p className="text-xs text-gray-400">No vehicles attached yet.</p>
            ) : (
              <div className="space-y-1.5">
                {orgVehicles.map(v => (
                  <div key={v.vehicle_id} className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded p-2 text-sm">
                    <div>
                      <p className="font-bold">{v.vehicle_name}</p>
                      <p className="text-[10px] text-gray-500">{v.category} · {v.tag} · {f$(v.retail_hourly)}/hr retail</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-xs font-bold text-emerald-700">{v.partner_revenue_share_percent || org.default_revenue_share_percent}% to partner</span>
                      <button onClick={() => detach(v.vehicle_id)} className="text-gray-400 hover:text-red-600" data-testid={`pn-detach-${v.vehicle_id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {available.length > 0 && (
            <div>
              <p className="text-xs uppercase tracking-widest font-bold text-gray-500 mb-2">Available Vehicles to Attach ({available.length})</p>
              <div className="space-y-1.5 max-h-72 overflow-y-auto">
                {available.map(v => (
                  <div key={v.vehicle_id} className="flex items-center justify-between bg-white border border-gray-200 rounded p-2 text-sm">
                    <div>
                      <p className="font-semibold">{v.vehicle_name}</p>
                      <p className="text-[10px] text-gray-500">{v.category} · {v.tag}</p>
                    </div>
                    <button onClick={() => attach(v.vehicle_id)} className="text-xs font-bold text-red-600 hover:text-red-700 flex items-center gap-1" data-testid={`pn-attach-${v.vehicle_id}`}>
                      <Plus className="h-3 w-3" /> Attach
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="border-t border-gray-200 pt-4">
            <Button variant="outline" onClick={del} className="text-red-600 border-red-300 hover:bg-red-50 h-9 text-xs" data-testid="pn-delete-org">
              <Trash2 className="h-3 w-3 mr-1" /> Delete Partner
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function RevenueSplitDrawer({ split, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-stretch justify-end" onClick={onClose}>
      <div className="bg-white w-full max-w-2xl h-full overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-gray-200 px-5 py-4 flex items-start justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">Revenue Split · {split.month}</p>
            <h3 className="text-xl font-black mt-1">{split.total_bookings} bookings</h3>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 p-1"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-3 gap-2">
            <Kpi label="Gross" value={f$(split.gross_revenue)} />
            <Kpi label="Partner" value={f$(split.partner_revenue)} color="amber" />
            <Kpi label="ACE" value={f$(split.ace_revenue)} color="emerald" />
          </div>
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-3 py-2 font-semibold text-[10px] uppercase tracking-wider text-gray-600">Date</th>
                <th className="text-left px-3 py-2 font-semibold text-[10px] uppercase tracking-wider text-gray-600">Client</th>
                <th className="text-left px-3 py-2 font-semibold text-[10px] uppercase tracking-wider text-gray-600">Vehicle</th>
                <th className="text-right px-3 py-2 font-semibold text-[10px] uppercase tracking-wider text-gray-600">Total</th>
                <th className="text-right px-3 py-2 font-semibold text-[10px] uppercase tracking-wider text-gray-600">Partner</th>
                <th className="text-right px-3 py-2 font-semibold text-[10px] uppercase tracking-wider text-gray-600">ACE</th>
              </tr>
            </thead>
            <tbody>
              {split.bookings.map(b => (
                <tr key={b.booking_id} className="border-b border-gray-100">
                  <td className="px-3 py-2 text-xs">{b.event_date}</td>
                  <td className="px-3 py-2 text-xs">{b.client_name}</td>
                  <td className="px-3 py-2 text-xs">{b.vehicle_name}</td>
                  <td className="px-3 py-2 text-right text-xs font-mono">{f$(b.total)}</td>
                  <td className="px-3 py-2 text-right text-xs font-mono text-amber-700">{f$(b.partner_revenue)}</td>
                  <td className="px-3 py-2 text-right text-xs font-mono text-emerald-700">{f$(b.ace_revenue)}</td>
                </tr>
              ))}
              {split.bookings.length === 0 && (
                <tr><td colSpan={6} className="text-center py-6 text-gray-400 text-xs">No partner-attributed bookings this month yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default PartnerNetworkTab;
